// Visor de foto ampliada.
//
// En la grilla las fotos entran en un cuadrado, así que una prenda
// vertical se ve chica. Acá se ve entera y grande.
//
// Detalles que parecen menores y no lo son:
//
//   - Se cierra con Escape, con clic afuera y con el botón. Tres
//     salidas, porque una sola es la que alguien no encuentra.
//   - Bloquea el scroll del fondo mientras está abierto. Sin eso, en
//     el celular se desplaza la lista de atrás y al cerrar aparecés en
//     otro lugar del armario.
//   - El clic sobre la imagen NO cierra: es el gesto natural para
//     mirarla de cerca, y cerrar ahí frustra.
//
// LO QUE SE AGREGÓ EL 2026-08-20
//
// Cuando se le pasa la prenda —no solo la foto— el visor muestra
// también cuándo fue la última vez que la usaste, y ofrece abrir su
// ficha para editarla.
//
// La ficha se abre ENCIMA del visor y al cerrarla volvés al visor, no
// a la grilla. Es el pedido textual: "al salir de la ficha volver al
// lugar inicial". Perder el lugar es lo que hace que uno no se moleste
// en corregir un dato.
//
// `prenda` es opcional a propósito: la carga por lote usa el visor para
// mostrar una foto que todavía no es ninguna prenda.

import { useEffect, useState } from "react";
import { haceCuanto } from "./fechas";
import FichaPrenda from "./FichaPrenda";
import { t, useIdioma, idiomaActual } from "./i18n";
import { IconoFichada } from "./Iconos";

export default function Visor({ url, alt, prenda, onCerrar, onPrendaEditada }) {
  useIdioma();
  const [verFicha, setVerFicha] = useState(false);

  // Al cerrar el visor entero, la ficha no puede quedar viva.
  useEffect(() => {
    if (!url) setVerFicha(false);
  }, [url]);

  useEffect(() => {
    if (!url) return;

    const alTeclear = (e) => {
      // Con la ficha abierta, Escape lo maneja ella: si lo manejaran
      // las dos, una sola tecla cerraría los dos modales de golpe y
      // volverías a la grilla sin haberlo pedido.
      if (e.key === "Escape" && !verFicha) onCerrar();
    };

    const scrollPrevio = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", alTeclear);

    return () => {
      document.body.style.overflow = scrollPrevio;
      window.removeEventListener("keydown", alTeclear);
    };
  }, [url, onCerrar, verFicha]);

  if (!url) return null;

  /* El último uso puede venir con el nombre de la columna (`ultimo_uso`,
     del armario y del historial) o con el del motor (`ultimoUso`, de las
     sugerencias). Se aceptan los dos en vez de obligar a cada pantalla a
     traducir.

     ⚠️  Y SE DISTINGUE "NO LO SÉ" DE "NUNCA SE USÓ" (2026-08-27).

     Las dos ausentes = ninguna ruta consultó el uso → no se dice nada.
     Presente y null = se consultó y nunca se usó → "Sin usar todavía".

     Antes las dos cosas caían en null y el visor afirmaba lo segundo.
     El dueño lo vio donde peor queda: abrió el HISTORIAL —outfits que
     él registró—, tocó una prenda, y le dijo que no la había usado. El
     campo no salía del servidor: faltaba en la lista blanca de la
     consulta. Una pantalla que no tiene un dato tiene que callarse, no
     inventar el caso vacío. */
  const sabemosElUso =
    prenda != null &&
    (prenda.ultimo_uso !== undefined || prenda.ultimoUso !== undefined);
  const ultimoUso = prenda?.ultimo_uso ?? prenda?.ultimoUso ?? null;

  return (
    <>
      <div
        className="visor"
        onClick={onCerrar}
        role="dialog"
        aria-modal="true"
        aria-label={alt || t("visor.fotoAmpliada", "Foto ampliada")}
      >
        <button
          type="button"
          className="visor-cerrar"
          onClick={onCerrar}
          aria-label={t("comun.cerrar", "Cerrar")}
        >
          ×
        </button>

        {/* ⚠️  ESTA FOTO NO ES UN BOTÓN, Y LO DECÍA (BA-14, 2026-09-06).
            Tenía role="button", tabIndex y manejo de teclado, puestos
            por una pasada mecánica sobre "img con onClick". Pero su
            onClick solo frena la propagación para que tocar la foto no
            cierre el visor: no hace NADA. El resultado era un lector de
            pantalla anunciando "botón" sobre algo inerte, y una parada
            de tabulación de más en el medio del visor. Un control falso
            es peor que ninguno. */}
        <img
          src={url}
          alt={alt || ""}
          className="visor-foto"
          onClick={(e) => e.stopPropagation()}
        />

        {/* El pie no cierra al tocarlo: tiene un botón adentro y un pie
            que se cierra solo al apuntarle sería una trampa. */}
        <div className="visor-pie" onClick={(e) => e.stopPropagation()}>
          {alt && <p className="visor-nombre">{alt}</p>}

          {/* El renglón existe si hay ALGO que decir. El ícono de
              fichada se sabe en todas las pantallas; el último uso no,
              así que el renglón puede llevar solo el ícono. */}
          {prenda && (sabemosElUso || prenda.fichada === true) && (
            <p className="visor-uso">
              {/* "Sin usar todavía" y no "Sin usar": la primera dice que
                  todavía puede pasar; la segunda suena a reproche. */}
              {sabemosElUso &&
                t("visor.ultimaVez", "Última vez: {cuando}", {
                  cuando: haceCuanto(ultimoUso, {
                    nunca: t("visor.sinUsar", "Sin usar todavía"),
                    idioma: idiomaActual(),
                  }),
                })}
              {/* Solo el ícono, sin texto: acá no se ficha ni se
                  desficha, se informa. El rótulo va en el `title` para
                  que se pueda averiguar qué significa sin salir de la
                  pantalla, y en `aria-label` para quien no lo ve. */}
              {prenda?.fichada === true && (
                <span
                  title={t("ficha.fichada", "Prenda fichada")}
                  aria-label={t("ficha.fichada", "Prenda fichada")}
                  role="img"
                  style={{ color: "var(--acento)", marginInlineStart: "var(--e2)" }}
                >
                  <IconoFichada relleno />
                </span>
              )}
            </p>
          )}

          {prenda?.id && (
            <button
              type="button"
              className="visor-ficha"
              onClick={() => setVerFicha(true)}
            >
              {t("visor.verFicha", "Ver ficha de la prenda")}
            </button>
          )}
        </div>
      </div>

      {verFicha && prenda?.id && (
        <FichaPrenda
          prendaId={prenda.id}
          onCerrar={() => setVerFicha(false)}
          onGuardada={onPrendaEditada}
        />
      )}
    </>
  );
}
